import ProductDetails from '@Components/ProductDetails';
import ProductMedia from '@Components/ProductMedia';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import Check from 'public/check_mark.svg';
import Close from 'public/close.svg';
import Arrow from 'public/show-arrow.svg';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Col, Row } from 'styled-bootstrap-grid';
import { APIResponse } from 'types/APIResponse';
import { Product } from 'types/Products';
import { Rate } from 'types/Rate';
import { Review } from 'types/Review';
import { Badge, BrowseBar, CloseIcon, More, MoreArrow, NotCompatible, Section } from './ProductDetailsSection.styled';

type Props = {
  data: Product;
  reviewData?: APIResponse<Review[]>;
  rateData?: Rate;
};

export default function ProductDetailsSection({ data, rateData, reviewData }: Props) {
  const { data: profileCars } = useSelector((state: RootState) => state.profileCars);

  const { t } = useTranslation('Product');
  const hasDiscount = useMemo(
    () => data.price && data.special_price && data.price - data.special_price !== 0,
    [data.price, data.special_price]
  );
  const favoriteCar = useMemo(() => profileCars?.find((car) => car.is_favorite), [profileCars]);
  const [hideCarCompatibility, setHideCarCompatibility] = useState(false);
  const isCarCompatible = useMemo(
    () => data.car_ids?.some((key) => key === favoriteCar?.car.id),
    [data.car_ids, favoriteCar?.car.id]
  );

  return (
    <Section>
      {hasDiscount && (
        <Badge>
          {Math.ceil(100 - ((data.special_price as number) * 100) / data.price)}%<span>{t('off')}</span>
        </Badge>
      )}
      {!hideCarCompatibility && favoriteCar && data.car_ids && (
        <BrowseBar compatible={isCarCompatible}>
          <More href={isCarCompatible ? `/products/car/${favoriteCar.car.id}` : '/products'}>
            <MoreArrow>
              <Image src={Arrow} width={6.58} height={11.87} alt="more arrow" priority={true} />
            </MoreArrow>
            {isCarCompatible ? t('browse_compatible') : t('show_more')}
          </More>
          <NotCompatible>
            {isCarCompatible ? t('compatible') : t('not_compatible')}
            <CloseIcon onClick={() => (isCarCompatible ? null : setHideCarCompatibility(true))}>
              <Image src={isCarCompatible ? Check : Close} alt="close icon" />
            </CloseIcon>
          </NotCompatible>
        </BrowseBar>
      )}
      <Row>
        <Col col={12} md={5}>
          <ProductMedia data={data} />
        </Col>
        <Col col={12} md={7}>
          <ProductDetails data={data} rateData={rateData} reviewData={reviewData} />
        </Col>
      </Row>
    </Section>
  );
}
